import { jfetch, snakeCaseKeys } from "../utils";
import { inRange, clamp, mean, pick, compact } from "lodash";
import { percentOfNum, percentOfTotal } from "../utils/numbers";
import {
  getAttributes,
  normalizeApiArrayResponse,
  normalizeApiResponse,
  normalizeRecord,
} from "./api_utils";
import { patchUser } from "./user";
import QuoteCalculator from "../components/pages/QuoteCalculator";

const hunterFarmerCommissionTemplate: ICommissionConfigAttributes = {
  name: "Hunter/Farmer",
  annuitiesQuotaThresholds: [
    {
      quotaStart: 0,
      percentPaid: 0,
    },
    {
      quotaStart: 8000,
      percentPaid: 1,
    },
    {
      quotaStart: 30000,
      percentPaid: 2,
    },
    {
      quotaStart: 50000,
      percentPaid: 2.5,
    },
    {
      quotaStart: 100000,
      percentPaid: 3,
    },
  ],
  agreementsQuota: 4000,
  agreementsMaxPercent: 125,
  agreementsQuotaThresholds: [
    { quotaStart: 0 },
    { quotaStart: 25 },
    { quotaStart: 50 },
    { quotaStart: 75 },
    { quotaStart: 100 },
    { quotaStart: 105 },
  ],
  laborQuota: 5000,
  laborQuotaThresholds: [
    {
      quotaStart: 0,
      percentPaid: 0,
    },
    {
      quotaStart: 20,
      percentPaid: 6,
    },
    {
      quotaStart: 60,
      percentPaid: 8,
    },
    {
      quotaStart: 100,
      percentPaid: 10,
    },
    {
      quotaStart: 125,
      percentPaid: 12,
    },
  ],
  hardwareSoftwareQuota: 5000,
  hardwareQuotaThresholds: [
    {
      quotaStart: 0,
      percentPaid: 0,
    },
    {
      quotaStart: 20,
      percentPaid: 6,
    },
    {
      quotaStart: 60,
      percentPaid: 7,
    },
    {
      quotaStart: 100,
      percentPaid: 8,
    },
    {
      quotaStart: 125,
      percentPaid: 10,
    },
  ],
};

export const commissionTemplates = {
  HUNTER_FARMER: hunterFarmerCommissionTemplate,
};
export const commissionConfigDefault = hunterFarmerCommissionTemplate;

export const newCommissionMonthInputs: ICommissionInputs = {
  annuities: 0,
  agreements: 0,
  hardwareSoftware: 0,
  labor: 0,
};

const configPathBase = "/commission_calculator/configs";

export function configPath(configId) {
  return `${configPathBase}/${configId}`;
}

export function getCommissionConfigs(orgId): Promise<ICommissionConfig[]> {
  return jfetch
    .get(configPathBase, { org_id: orgId })
    .then(resp => resp.data.map(c => normalizeRecord(c)));
}

export function normalizeCommissionConfig(record: ICommissionConfigRecord) {
  return normalizeRecord<ICommissionConfig>(record);
}

export function getCommissionConfig(
  configId: string
): Promise<ICommissionConfig> {
  return jfetch
    .get(configPath(configId))
    .then(resp => normalizeCommissionConfig(resp.data));
}

export function createCommissionConfig(config: Dictionary) {
  return jfetch.post<IJsonApiResponse<ICommissionConfigRecord>>(
    configPathBase,
    { commission_calculator_config: snakeCaseKeys(config) }
  );
}

export function updateCommissionConfig(config: ICommissionConfig) {
  return jfetch
    .patch(configPath(config.id), {
      commission_calculator_config: snakeCaseKeys(getAttributes(config)),
    })
    .then(resp => normalizeCommissionConfig(resp.data));
}

export function addEmployeeToCommissionConfig(employeeId, configId) {
  return jfetch.post(`${configPath(configId)}/add_employee`, {
    employee_id: employeeId,
  });
}

export function removeEmployeeFromCommissionConfig(employeeId, configId) {
  return jfetch.post(`${configPath(configId)}/remove_employee`, {
    employee_id: employeeId,
  });
}

export function updateEmployeeCommissionConfig(userId, commissionConfigId) {
  return patchUser(userId, { commissionConfigId });
}

export function deleteCommissionConfig(configId) {
  return jfetch.delete(configPath(configId));
}

export function archiveCommissionConfig(configId) {
  return jfetch
    .patch(configPath(configId), {
      commission_calculator_config: { archived: true },
    })
    .then(normalizeApiResponse);
}

export function normalizeCommissionMonth(monthRecord): ICommissionMonth {
  return normalizeRecord(monthRecord);
}

export function getEmployeeCommissions(userId) {
  return jfetch
    .get(`/commission_calculator/months/user_commissions/${userId}`)
    .then(normalizeApiArrayResponse);
}

export function getEmployeeListCommissions(employeeList) {
  return jfetch
    .get(`/commission_calculator/months/`, employeeList)
    .then(normalizeApiArrayResponse);
}

export function getEmployeeCommission(monthId) {
  return jfetch
    .get(`/commission_calculator/months/${monthId}`)
    .then(normalizeApiResponse);
}

export function createEmployeeCommission(userId, commissionAttributes) {
  return jfetch.post(`/commission_calculator/months`, {
    commission_calculator_month: snakeCaseKeys({
      userId,
      ...commissionAttributes,
    }),
  });
}

export function getCommissionOrgConfig(orgId) {
  return jfetch
    .get(`/commission_calculator/org_configs/${orgId}`)
    .then(normalizeApiResponse);
}

export function updateCommissionOrgConfig(orgId, config) {
  return jfetch.put(`/commission_calculator/org_configs/${orgId}`, {
    commission_calculator_org_config: snakeCaseKeys(getAttributes(config)),
  });
}

export function extractInputsFromMonth(
  month: ICommissionMonth
): ICommissionInputs {
  return pick(month, ["annuities", "agreements", "labor", "hardwareSoftware"]);
}

export function patchCommission(commissionMonthId, attributes) {
  return jfetch.patch(`/commission_calculator/months/${commissionMonthId}`, {
    commission_calculator_month: snakeCaseKeys(attributes),
  });
}

export function addReviewComment(commissionMonthId, text) {
  return jfetch.post(
    `/commission_calculator/months/${commissionMonthId}/add_comment`,
    {
      text,
    }
  );
}

export function getCommissionsMonthStatus(orgId) {
  return jfetch
    .get("/commission_calculator/months/month_status", {
      org_id: orgId,
    })
    .then(resp => {
      return {
        usersMissingCommissions: normalizeApiArrayResponse(
          resp.users_missing_commissions
        ),
        commissions: normalizeApiArrayResponse(resp.commissions),
      };
    });
}

export function getCommissionsByStatus(orgId, status) {
  return jfetch
    .get("/commission_calculator/months/by_status", {
      org_id: orgId,
      status: status,
    })
    .then(resp => {
      return normalizeApiArrayResponse(resp.commissions);
    });
}

export function getCommissionPercentile(startDate, endDate, value) {
  return jfetch
    .get(`/commission_calculator/months/commission_percentile`, {
      start_date: startDate,
      end_date: endDate,
      ...value,
    })
    .then(resp => resp);
}

export function getCommissionAverages(startDate, endDate) {
  return jfetch
    .get(`/commission_calculator/months/core_commission_averages`, {
      start_date: startDate,
      end_date: endDate,
    })
    .then(resp => resp);
}

/**
 * finds the percentPaid value from the correct threshold for a given amount
 * @param amount
 * @param thresholds
 * @param quota
 */
function quotaPercentPaidFromThresholds(
  amount,
  thresholds: IQuotaThresholds,
  quota?: number
): number {
  let percentPaid = 0;

  // labor and hardware/software quotas are based on percent of quota,
  // so we need to pass that in and use that to calculate the ranges if passed
  thresholds
    .map(t => ({
      threshold: quota ? percentOfNum(t.quotaStart, quota) : t.quotaStart,
      ...t,
    }))
    .some((t, i, a) => {
      let range;
      if (i === a.length - 1) {
        range = [t.threshold, Infinity];
      } else {
        range = [t.threshold, a[i + 1].threshold - 0.01];
      }

      if (inRange(amount, range[0], range[1])) {
        percentPaid = t.percentPaid;
        return true;
      } else {
        return false;
      }
    });
  return percentPaid;
}

export function calculateCommission(
  month: ICommissionInputs,
  config: ICommissionConfig
) {
  const quotaEnabled = config.disabledQuotas
    ? quotaName => !config.disabledQuotas.includes(quotaName)
    : () => true;

  // agreements numbers
  const agreements: Partial<ICommissionTypeResult> = {};
  agreements.percentQuota = percentOfTotal(
    month.agreements,
    config.agreementsQuota
  );
  agreements.percentPaid = clamp(
    agreements.percentQuota,
    config.agreementsMaxPercent
  );
  agreements.commission = percentOfNum(
    agreements.percentPaid,
    month.agreements
  );

  // labor numbers
  const labor: Partial<ICommissionTypeResult> = {};
  labor.percentQuota = percentOfTotal(month.labor, config.laborQuota);
  labor.percentPaid = quotaPercentPaidFromThresholds(
    month.labor,
    config.laborQuotaThresholds,
    config.laborQuota
  );
  labor.commission = percentOfNum(labor.percentPaid, month.labor);

  // hardware/software numbers
  const hardwareSoftware: Partial<ICommissionTypeResult> = {};
  hardwareSoftware.percentQuota = percentOfTotal(
    month.hardwareSoftware,
    config.hardwareSoftwareQuota
  );
  hardwareSoftware.percentPaid = quotaPercentPaidFromThresholds(
    month.hardwareSoftware,
    config.hardwareQuotaThresholds,
    config.hardwareSoftwareQuota
  );
  hardwareSoftware.commission = percentOfNum(
    hardwareSoftware.percentPaid,
    month.hardwareSoftware
  );  

  let quotasToAverage = [
    quotaEnabled("agreements") ? agreements.percentQuota : null,
    quotaEnabled("labor") ? labor.percentQuota : null,
    quotaEnabled("hardwareSoftware") ? hardwareSoftware.percentQuota : null,
  ];  

  
  quotasToAverage = quotasToAverage.filter((element) => {
    return element !== null
  })


  const quotaAverage = quotasToAverage.length > 0 ? mean(quotasToAverage) : 0;

  // annuities numbers
  const annuities: Partial<ICommissionTypeResult> = {};
  annuities.percentPaid = quotaPercentPaidFromThresholds(
    month.annuities,
    config.annuitiesQuotaThresholds
  );
  if (quotaAverage <= 100) {
    annuities.commission = percentOfNum(
      quotaAverage,
      percentOfNum(annuities.percentPaid, month.annuities)
    );
  } else {
    annuities.commission = percentOfNum(annuities.percentPaid, month.annuities);
  }

  let total = 0;

  if (quotaEnabled("annuities")) {
    total += annuities.commission;
  }

  if (quotaEnabled("agreements")) {
    total += agreements.commission;
  }

  if (quotaEnabled("labor")) {
    total += labor.commission;
  }

  if (quotaEnabled("hardwareSoftware")) {
    total += hardwareSoftware.commission;
  }

  return {
    annuities,
    agreements,
    labor,
    hardwareSoftware,
    quotaAverage,
    total,
  };
}

export async function onFileDrop(acceptedFiles: File[], commissionMonthId) {
  const data = new FormData();
  acceptedFiles.forEach(file => data.append("commission_attachment[]", file));
  const result = await jfetch.post(
    `/commission_calculator/months/${commissionMonthId}/commission_attachment`,
    data
  );
  return normalizeApiResponse(result);
}



// WEBPACK FOOTER //
// ./app/javascript/libs/commission_calculator.ts